import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import PlantillaServicio from "../PlantillaServicio";
import { FileText } from "lucide-react";
import ModalRegistroProceso from "../../components/estudiante/ModalRegistroProceso";
import api from "../../axiosConfig"; // ✅ CAMBIO CRÍTICO: Usar instancia con interceptores
import TablaDocumentos from "../../components_student/TablaDocumentoss";

const EstadiasNacionales = () => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [procesoActivo, setProcesoActivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodoExpirado, setPeriodoExpirado] = useState(false);
  const [todosBloqueados, setTodosBloqueados] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));

  const normalizeText = (value) =>
    (value ?? "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const fetchProcesoActivo = async () => {
    setLoading(true);
    setError(null);
    setPeriodoExpirado(false);
    setTodosBloqueados(false);
    try {
      if (!user?.id) throw new Error("Usuario no autenticado");

      // ✅ CORREGIDO: Obtener todos los periodos con autenticación
      const { data: periodos } = await api.get("/periodos");
      const periodoActivo = periodos.find((p) => p.EstadoActivo === "Activo");
      setPeriodoExpirado(!periodoActivo);

      // ✅ CORREGIDO: Obtener estados de los formatos con autenticación
      const { data: formatos } = await api.get("/documentosAdmin");
      const hayFormatosActivos = formatos.some((f) => f.estado === "Activo");
      if (!periodoActivo && !hayFormatosActivos) setTodosBloqueados(true);

      const periodoId = periodoActivo?.IdPeriodo || (periodos.length > 0 ? periodos[periodos.length - 1].IdPeriodo : null);
      if (!periodoId) throw new Error("No hay periodos disponibles.");

      // ✅ CORREGIDO: Validar proceso con autenticación
      const { data } = await api.get(`/procesos/validar/${user.id}/${periodoId}`);
      
      if (data.registrado) {
        const expectedTipo = normalizeText("Estadía Nacional");
        const actualTipo = normalizeText(data?.proceso?.tipo_proceso);
        if (actualTipo === expectedTipo) {
          setIsRegistered(true);
          setProcesoActivo(data.proceso);
        } else if (data.proceso.tipo_proceso) {
          setError(`Ya estás registrado en ${data.proceso.tipo_proceso} para este periodo.`);
          setIsRegistered(false);
          setProcesoActivo(null);
        } else {
          setIsRegistered(false);
          setProcesoActivo(data.proceso);
          setShowModal(true);
        }
      } else {
        setIsRegistered(false);
        setProcesoActivo(null);
        setShowModal(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al verificar el proceso.");
      setIsRegistered(false);
      setProcesoActivo(null);
      console.error("Error al verificar registro (Estadía Nacional):", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcesoActivo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSuccess = async () => {
    setIsRegistered(true);
    await fetchProcesoActivo();
    handleCloseModal();
  };

  return (
    <PlantillaServicio
      titulo="Estadías Nacionales"
      descripcion="Gestiona tu proceso de Estadías Nacionales, sube tus documentos y da seguimiento a tu progreso académico."
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
      >
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4 flex justify-between items-center"
          >
            <span>{error}</span>
          </motion.div>
        )}

        {loading && (
          <div className="text-center text-gray-500 animate-pulse">
            Verificando registro...
          </div>
        )}

        {!loading && (
          <>
            {!isRegistered && !error && (
              <motion.button
                onClick={handleOpenModal}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto bg-gradient-to-r from-red-900 to-red-700 text-white px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center gap-3 mb-6 shadow-md hover:from-red-800 hover:to-red-600"
              >
                <FileText className="w-5 h-5" />
                <span className="font-semibold">Registrar Estadía Nacional</span>
              </motion.button>
            )}

            <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden border border-gray-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gradient-to-r from-red-900 to-red-700 text-white">
                <h2 className="text-white sm:text-2xl font-semibold">
                  Documentos Requeridos
                </h2>
              </div>

              {(periodoExpirado || todosBloqueados) && isRegistered && procesoActivo && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-2 rounded-t-lg text-center"
                >
                  <span>⚠️ {todosBloqueados ? "No hay periodo activo ni formatos manualmente activados. Todos los documentos están bloqueados." : "El periodo ha expirado. Los documentos manualmente activados están disponibles."}</span>
                </motion.div>
              )}

              {isRegistered && procesoActivo ? (
                <div className="p-4">
                  <TablaDocumentos tipoProceso="Estadía Nacional" procesoId={procesoActivo.id_proceso} todosBloqueados={todosBloqueados} />
                </div>
              ) : (
                <div className="p-4 text-gray-500 italic">
                  Regístrate para comenzar a subir tus documentos de Estadía Nacional.
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>

      {showModal && (
        <ModalRegistroProceso
          open={showModal}
          onClose={handleCloseModal}
          tipoProceso="Estadía Nacional"
          procesoExistente={procesoActivo}
          onSuccess={handleSuccess}
        />
      )}
    </PlantillaServicio>
  );
};

export default EstadiasNacionales;
